import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import { MAILBOX_MESSAGE_QUERY_KEY } from '../../message-detail/hooks/useMailboxMessage';
import { MAILBOX_INTENTS_QUERY_KEY } from '../../../hooks/useMailboxIntentsQuery';
import {
    getMailboxIntentItems,
    mailboxMessageHasLinkedCase,
    buildMailboxApplyIntentPayload,
    findMailboxLinkedCase,
    getMailboxIntentItem,
    getMessageCases,
    mapMailboxActionError,
    normalizeMailboxLinkedCase,
} from '../../../helpers';
import { MAILBOX_MESSAGES_QUERY_KEY } from './useMailboxMessages';
import { showMailboxCaseCreatedToast } from '../../MailboxCaseCreatedToast';

const useMailboxFolderIntents = ({
    t,
}) => {
    const service = useMailboxService();
    const queryClient = useQueryClient();
    const location = useLocation();
    const history = useHistory();
    const [applying, setApplying] = useState(null);

    const refreshIntentQueries = (messageIds = []) => Promise.all([
        queryClient.invalidateQueries([MAILBOX_MESSAGES_QUERY_KEY]),
        queryClient.invalidateQueries([MAILBOX_INTENTS_QUERY_KEY]),
        ...messageIds.map((messageId) => (
            queryClient.invalidateQueries([MAILBOX_MESSAGE_QUERY_KEY, messageId])
        )),
    ]);

    const applyIntent = async (messageId, intent, existingCases = [], analysis = null, message = null) => {
        if (!messageId || applying) {
            return findMailboxLinkedCase(existingCases, intent);
        }

        const existing = findMailboxLinkedCase(existingCases, intent);

        if (existing) {
            return existing;
        }

        if (mailboxMessageHasLinkedCase(message, existingCases)) {
            toast.error(t('A case has already been created from this email.'));
            return null;
        }

        setApplying({
            messageId,
            intent,
        });

        try {
            const payload = await service.applyMessageIntent(
                messageId,
                buildMailboxApplyIntentPayload(intent, analysis)
            );
            const created = normalizeMailboxLinkedCase(payload, intent);

            await refreshIntentQueries([messageId]);

            showMailboxCaseCreatedToast(t, created || { intent }, location, history);

            return created || { intent };
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(mapMailboxActionError(error, t));
            return null;
        } finally {
            setApplying(null);
        }
    };

    const applyIntentToMessages = async (items, intent) => {
        if (applying) {
            return [];
        }

        const itemsWithId = (items || []).filter((item) => item?.messageId);
        const targets = itemsWithId.filter(({ existingCases = [], message } = {}) => (
            !mailboxMessageHasLinkedCase(message, existingCases)
        ));

        if (!itemsWithId.length) {
            toast.info(t('Select at least one message.'));
            return [];
        }

        if (!targets.length) {
            toast.info(t('Selected messages already have a linked case.'));
            return [];
        }

        setApplying({
            messageId: 'bulk',
            intent,
        });

        try {
            const results = await Promise.allSettled(
                targets.map(({ messageId }) => service.applyMessageIntent(messageId, {
                    intent,
                }))
            );
            const succeededIds = targets
                .filter((_, index) => results[index].status === 'fulfilled')
                .map((target) => target.messageId);
            const failed = results.find((result) => result.status === 'rejected');

            await refreshIntentQueries(succeededIds);

            if (succeededIds.length === 1) {
                const createdResult = results.find((result) => result.status === 'fulfilled');
                showMailboxCaseCreatedToast(
                    t,
                    normalizeMailboxLinkedCase(createdResult?.value, intent) || { intent },
                    location,
                    history
                );
            } else if (succeededIds.length) {
                const successKey = getMailboxIntentItem(intent)?.successKey
                    || 'Created case from mailbox email';

                toast.success(t(successKey));
            }

            if (failed) {
                toast.error(mapMailboxActionError(failed.reason, t));
            }

            return succeededIds;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(mapMailboxActionError(error, t));
            return [];
        } finally {
            setApplying(null);
        }
    };

    return {
        intents: getMailboxIntentItems(),
        applying,
        isApplying: Boolean(applying),
        applyIntent,
        applyIntentToMessages,
        getCases: getMessageCases,
    };
};

export default useMailboxFolderIntents;
